/**
 * 折れ目(`VideoFrame.folds`)の扱い。
 *
 * 正本は [ADR-0057](../../../docs/adr/0057-folded-frame.md)、
 * 持ち方は [#28 の DESIGN.md](../../../docs/issues/28-data-design/DESIGN.md)。
 *
 * **ここに置くのは構造の規則だけ。** 境目の位置・継ぎ目のそろえ方・面の割り付けは
 * ホモグラフィの知識なので**描画コア**に置く(ADR-0057 の決定 5)。
 *
 * **S04 と業務 API が同じ関数を使う。** UI が通した値を API が弾くと
 * **自動保存が止まり、ほかの編集まで保存されない**([ADR-0035](../../../docs/adr/0035-autosave-by-polling.md))。
 */
/**
 * **折れた枠か。** `folds` があり、境目が 1 本以上。
 *
 * **判定はこの関数だけで行う。** S04・描画コア・業務 API・`linkedCells` が
 * 別々に `folds != null` などと書くと、**空の `lines` の扱いが食い違う。**
 *
 * **型ガードにしている** —— 真なら `folds` が在ると分かるので、呼び出し側で `null` の判定を重ねずに済む。
 *
 * **述語は「境目が 1 本以上の `folds`」まで言う。** `folds: Folds` とだけ主張すると、`folds` が必須の型
 * (描画コアの枠など)で偽になったとき TS が `never` に絞り、**境目が空の枠(実行時には届く)の分岐が
 * 到達しない扱いになる。** 空でない組で主張すれば、偽の側は元の型のまま残る。
 */
export function isFolded(frame) {
    return frame.folds != null && frame.folds.lines.length > 0;
}
/**
 * 折る向きの項の数。**分割していない枠は 1**(境目が無い)。
 *
 * `'columns'` は縦の境目なので `grid.columns`、`'rows'` は横の境目なので `grid.rows`。
 */
function termCountOf(grid, axis) {
    return grid == null ? 1 : grid[axis].length;
}
/**
 * **構造として受け付けられない理由の一覧。** 空なら受け付けられる。
 *
 * 見るのは**分割との対応だけ** —— 折る向きに境目があるか・番号が範囲に収まるか・重複していないか。
 * 番号が整数か・座標が有限数かは**型の検査**(業務 API の zod)の仕事。
 *
 * **引き継ぎはしない。** 分割を変えたときに番号で引き継ぐのは S04 の仕事([`carryFolds`](#carryFolds))で、
 * ここで範囲外を黙って落とすと**古いタブの保存で折れ目が消える。** 弾いて気づかせる(`cells` と同じ)。
 *
 * **形の正しさ(面が潰れる・ねじれる)は見ない** —— 4 隅と同じく、描画コアがその面を描かないだけ(同意書の仮定 F)。
 */
export function foldsProblems(folds, grid) {
    if (folds == null)
        return [];
    const terms = termCountOf(grid, folds.axis);
    /*
     * **境目が無ければ番号は見ない** —— 「-1 以下にしてください」という意味の通らない指摘を並べない
     * (`cells` の範囲の検査と同じ判断)。
     *
     * **`lines` が空なら問題にしない。** 「無し」と同じ意味で、保存時に項目ごと落とす(`normalizeFolds`)。
     */
    if (folds.lines.length === 0)
        return [];
    if (terms < 2) {
        return [
            {
                path: ['folds', 'axis'],
                message: folds.axis === 'columns'
                    ? '縦の境目で折るには、横に 2 つ以上に分割してください'
                    : '横の境目で折るには、縦に 2 つ以上に分割してください',
            },
        ];
    }
    const problems = [];
    const seen = new Set();
    folds.lines.forEach((line, position) => {
        const path = ['folds', 'lines', position, 'index'];
        if (line.index >= terms - 1) {
            problems.push({ path, message: `境目の番号は ${String(terms - 2)} 以下にしてください` });
        }
        if (seen.has(line.index)) {
            problems.push({ path, message: '境目の番号が重複しています' });
        }
        seen.add(line.index);
    });
    return problems;
}
/**
 * 保存する形にそろえる。**`index` の昇順に並べ、境目が無ければ `undefined`。**
 *
 * **「無し」の表現を 1 つにする**(`normalizeCells` と同じ)。`{ axis, lines: [] }` が残ると、
 * 項目の無いドキュメントと**等しくないと判定され、保存した直後から未保存に見える**
 * (`web/src/pages/editor/state.ts` の `isEqualValue` は `[]` と `undefined` を区別する。`null` と項目なしは同じと見る)。
 */
export function normalizeFolds(folds) {
    if (folds == null || folds.lines.length === 0)
        return undefined;
    return { axis: folds.axis, lines: [...folds.lines].sort((a, b) => a.index - b.index) };
}
/**
 * 分割を変えたとき、**折った向きの境目を前から番号で引き継ぎ、余りは消す**(同意書の仮定 C)。
 *
 * - 項の**値**だけを変えた —— 境目の数は同じなので全部残る(比率は素材の当て方にだけ効き、置いた点は動かない)
 * - 項を**後ろに足した** —— 既存の境目はそのまま。足した境目は動かしていない扱い(描画コアが位置を求める)
 * - 項を**減らした** —— 範囲を外れた番号の境目を消す
 * - 折る向きの項が **1 つになった・分割を消した** —— 折れ目ごと消す(`undefined`)
 *
 * **間に項を挟むと番号がずれる**(ADR-0057 の「引き受ける制約」)。そのときは置き直してもらう。
 * **反対の向きの項を変えても折れ目は変わらない**(1 つの枠では片方の向きだけ折れる)。
 */
export function carryFolds(folds, to) {
    if (folds == null)
        return undefined;
    const terms = termCountOf(to, folds.axis);
    return normalizeFolds({
        axis: folds.axis,
        lines: folds.lines.filter((line) => line.index < terms - 1),
    });
}
//# sourceMappingURL=folds.js.map